import React, { useState, useCallback } from 'react';
import { TeacherAvatar } from '../../components/TeacherAvatar';
import { useTeacherAvatar } from '../../hooks/useTeacherAvatar';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { DndContext, closestCenter, useSensor, useSensors, PointerSensor } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, Landmark, RotateCcw, Star, Target, Info, Zap, Check, X, GripVertical } from 'lucide-react';
import { useLabProgress } from '../../hooks/useLabProgress';


const ERAS = {
  ancient: { label: 'Ancient India', color: '#f59e0b', range: '3000 BCE - 500 CE' },
  medieval: { label: 'Medieval India', color: '#8b5cf6', range: '500 CE - 1500 CE' },
  mughal: { label: 'Mughal & Maratha', color: '#ef4444', range: '1500 - 1800 CE' },
  modern: { label: 'Modern India', color: '#3b82f6', range: '1800 - 1947' },
  independent: { label: 'Independent India', color: '#22c55e', range: '1947 - Present' },
};

const MISSIONS = [
  {
    id: 1, name: 'Ancient India', description: 'Arrange ancient events in chronological order', stars: 1,
    events: [
      { id: 'e1', text: 'Indus Valley Civilization', year: '2500 BCE', order: 1 },
      { id: 'e2', text: 'Vedic Age — Rigveda composed', year: '1500 BCE', order: 2 },
      { id: 'e3', text: 'Birth of Gautama Buddha', year: '563 BCE', order: 3 },
      { id: 'e4', text: 'King Porus resists Alexander at Hydaspes', year: '326 BCE', order: 4 },
      { id: 'e5', text: 'Maurya Empire founded by Chandragupta', year: '322 BCE', order: 5 },
      { id: 'e6', text: 'Ashoka embraces Buddhism', year: '261 BCE', order: 6 },
      { id: 'e7', text: 'Gupta Empire - Golden Age', year: '320 CE', order: 7 },
      { id: 'e8', text: 'Yashodharman crushes Huna invader Mihirakula', year: '528 CE', order: 8 },
    ]
  },
  {
    id: 2, name: 'Rajput Resistance', description: 'Order the valiant stands of Indian kings against invaders', stars: 1,
    events: [
      { id: 'm1', text: 'Bappa Rawal founds Mewar, repels Arab invasion', year: '734 CE', order: 1 },
      { id: 'm2', text: 'Prithviraj Chauhan defeats Ghori at Tarain', year: '1191 CE', order: 2 },
      { id: 'm3', text: 'Rana Sanga leads Rajput confederacy at Khanwa', year: '1527 CE', order: 3 },
      { id: 'm4', text: 'Hemu crowned Hindu Emperor of Delhi', year: '1556 CE', order: 4 },
      { id: 'm5', text: 'Maharana Pratap\'s stand at Haldighati', year: '1576 CE', order: 5 },
      { id: 'm6', text: 'Lachit Borphukan crushes Mughal navy at Saraighat', year: '1671 CE', order: 6 },
    ]
  },
  {
    id: 3, name: 'Freedom Struggle', description: 'Arrange key events of Indian independence', stars: 2,
    events: [
      { id: 'f1', text: 'Revolt of 1857 (First War of Independence)', year: '1857', order: 1 },
      { id: 'f2', text: 'Indian National Congress founded', year: '1885', order: 2 },
      { id: 'f3', text: 'Jallianwala Bagh Massacre', year: '1919', order: 3 },
      { id: 'f4', text: 'Salt March (Dandi March)', year: '1930', order: 4 },
      { id: 'f5', text: 'Quit India Movement', year: '1942', order: 5 },
      { id: 'f6', text: 'Indian Independence', year: '1947', order: 6 },
    ]
  },
  {
    id: 4, name: 'Post-Independence', description: 'Key milestones of independent India', stars: 2,
    events: [
      { id: 'p1', text: 'Indian Constitution adopted', year: '1950', order: 1 },
      { id: 'p2', text: 'First General Elections', year: '1952', order: 2 },
      { id: 'p3', text: 'Green Revolution begins', year: '1965', order: 3 },
      { id: 'p4', text: 'Operation Flood (White Revolution)', year: '1970', order: 4 },
      { id: 'p5', text: 'Economic Liberalization', year: '1991', order: 5 },
      { id: 'p6', text: 'India launches Chandrayaan-1', year: '2008', order: 6 },
    ]
  },
  {
    id: 5, name: 'Mixed Challenge', description: 'Events from all eras - the ultimate test!', stars: 3,
    events: [
      { id: 'x1', text: 'Indus Valley Civilization', year: '2500 BCE', order: 1 },
      { id: 'x2', text: 'Maurya Empire founded', year: '322 BCE', order: 2 },
      { id: 'x3', text: 'Vijayanagara Empire founded', year: '1336 CE', order: 3 },
      { id: 'x4', text: 'Rana Sanga resists Babur at Khanwa', year: '1527', order: 4 },
      { id: 'x5', text: 'Revolt of 1857', year: '1857', order: 5 },
      { id: 'x6', text: 'Salt March', year: '1930', order: 6 },
      { id: 'x7', text: 'Indian Independence', year: '1947', order: 7 },
      { id: 'x8', text: 'Constitution adopted', year: '1950', order: 8 },
    ]
  },
  {
    id: 6, name: 'Maratha Empire', description: 'Arrange key events of the Maratha Empire', stars: 2,
    events: [
      { id: 'mr1', text: 'Shivaji establishes Maratha kingdom', year: '1674', order: 1 },
      { id: 'mr2', text: 'Sambhaji succeeds Shivaji', year: '1680', order: 2 },
      { id: 'mr3', text: 'Peshwa Baji Rao I expands territory', year: '1720', order: 3 },
      { id: 'mr4', text: 'Third Battle of Panipat', year: '1761', order: 4 },
      { id: 'mr5', text: 'Anglo-Maratha Wars begin', year: '1775', order: 5 },
      { id: 'mr6', text: 'Maratha Empire falls to British', year: '1818', order: 6 },
    ]
  },
  {
    id: 7, name: 'British India', description: 'Key events during British colonial rule', stars: 2,
    events: [
      { id: 'br1', text: 'Battle of Plassey (British control Bengal)', year: '1757', order: 1 },
      { id: 'br2', text: 'Permanent Settlement (land revenue)', year: '1793', order: 2 },
      { id: 'br3', text: 'First Railway in India (Mumbai-Thane)', year: '1853', order: 3 },
      { id: 'br4', text: 'Partition of Bengal', year: '1905', order: 4 },
      { id: 'br5', text: 'Rowlatt Act passed', year: '1919', order: 5 },
      { id: 'br6', text: 'Simon Commission arrives', year: '1928', order: 6 },
    ]
  },
  {
    id: 8, name: 'Science & Discovery', description: 'Indian contributions to science and discovery', stars: 2,
    events: [
      { id: 'sc1', text: 'Aryabhata calculates pi and Earth rotation', year: '499 CE', order: 1 },
      { id: 'sc2', text: 'Brahmagupta introduces zero', year: '628 CE', order: 2 },
      { id: 'sc3', text: 'Jagadish Chandra Bose demonstrates radio waves', year: '1895', order: 3 },
      { id: 'sc4', text: 'CV Raman discovers Raman Effect', year: '1928', order: 4 },
      { id: 'sc5', text: 'Homi Bhabha founds atomic research', year: '1945', order: 5 },
      { id: 'sc6', text: 'ISRO launches Mangalyaan to Mars', year: '2014', order: 6 },
    ]
  },
  {
    id: 9, name: 'Social Reformers', description: 'Arrange social reform movements chronologically', stars: 2,
    events: [
      { id: 'sr1', text: 'Raja Ram Mohan Roy founds Brahmo Samaj', year: '1828', order: 1 },
      { id: 'sr2', text: 'Sati abolished by Lord Bentinck', year: '1829', order: 2 },
      { id: 'sr3', text: 'Jyotirao Phule opens school for girls', year: '1848', order: 3 },
      { id: 'sr4', text: 'Swami Vivekananda at Parliament of Religions', year: '1893', order: 4 },
      { id: 'sr5', text: 'BR Ambedkar converts to Buddhism', year: '1956', order: 5 },
      { id: 'sr6', text: 'Right to Education Act', year: '2009', order: 6 },
    ]
  },
  {
    id: 10, name: 'Global Connections', description: 'Indian history in the context of world events', stars: 3,
    events: [
      { id: 'gl1', text: 'Rajendra Chola\'s navy conquers Srivijaya (SE Asia)', year: '1025 CE', order: 1 },
      { id: 'gl2', text: 'Vasco da Gama reaches India', year: '1498', order: 2 },
      { id: 'gl3', text: 'East India Company formed', year: '1600', order: 3 },
      { id: 'gl4', text: 'Indian troops in World War I', year: '1914', order: 4 },
      { id: 'gl5', text: 'INA formed by Subhas Chandra Bose', year: '1942', order: 5 },
      { id: 'gl6', text: 'India joins United Nations', year: '1945', order: 6 },
    ]
  },
];

function DraggableEvent({ event, isCorrect, isChecked }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: event.id });
  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    touchAction: 'none',
  };
  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}
      className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-grab active:cursor-grabbing transition-colors ${
        isDragging ? 'opacity-50 shadow-lg z-10 relative' :
        isChecked && isCorrect ? 'border-green-500/40 bg-green-500/10' :
        isChecked && !isCorrect ? 'border-red-500/40 bg-red-500/10' :
        'border-border/50 bg-card hover:border-primary/30'
      }`}>
      <GripVertical className="w-4 h-4 text-muted-foreground/40 shrink-0" />
      <div className="flex-1">
        <span className="text-sm font-medium">{event.text}</span>
      </div>
      <Badge variant="outline" className="text-xs shrink-0">{event.year}</Badge>
      {isChecked && (
        isCorrect ? <Check className="w-4 h-4 text-green-400 shrink-0" /> : <X className="w-4 h-4 text-red-400 shrink-0" />
      )}
    </div>
  );
}

function TimelineDropZone({ children }) {
  return (
    <div className="space-y-2 min-h-[200px] p-4 rounded-2xl border-2 border-dashed border-border/30" data-testid="timeline-zone">
      {children}
    </div>
  );
}

export default function HistoryLab() {
  const { showTeacher, dismiss, resummon, dismissed, avatarId } = useTeacherAvatar('history');
  const navigate = useNavigate();
  const { completedMissions, unlockedMissions, currentMission, setCurrentMission, completeMission: saveProgress, xp, loading: progressLoading } = useLabProgress('history', 10);
  const [userOrder, setUserOrder] = useState([]);
  const [isChecked, setIsChecked] = useState(false);
  const [result, setResult] = useState(null);

  const mission = MISSIONS[currentMission];

  React.useEffect(() => {
    // Shuffle events for the mission
    const shuffled = [...mission.events].sort(() => Math.random() - 0.5);
    setUserOrder(shuffled);
    setIsChecked(false);
    setResult(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMission]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setUserOrder(prev => {
      const oldIndex = prev.findIndex(e => e.id === active.id);
      const newIndex = prev.findIndex(e => e.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
    setIsChecked(false);
    setResult(null);
  };

  const checkOrder = () => {
    setIsChecked(true);
    const isCorrect = userOrder.every((e, i) => e.order === i + 1);
    if (isCorrect) {
      setResult('correct');
      if (!completedMissions.includes(currentMission)) {
        saveProgress(currentMission);
      }
    } else {
      setResult('incorrect');
    }
  };

  const shuffleEvents = () => {
    setUserOrder(prev => [...prev].sort(() => Math.random() - 0.5));
    setIsChecked(false);
    setResult(null);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="history-lab">
      <TeacherAvatar visible={showTeacher} avatarId={avatarId}
        text="History lab about Indian history timelines. Students drag historical events from Ancient, Medieval, and Modern India onto a timeline."
        cacheKey="lab-intro-history"
        onClose={dismiss} dismissed={dismissed} onResummon={resummon} />
      <header className="border-b border-border/50 bg-card/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/labs')} data-testid="back-to-labs">
              <ArrowLeft className="w-4 h-4 mr-2" /> Labs
            </Button>
            <div className="h-6 w-px bg-border" />
            <div className="flex items-center gap-2">
              <Landmark className="w-5 h-5 text-amber-400" />
              <span className="font-semibold">History Timeline Lab</span>
              <Badge variant="outline" className="text-xs">NCERT</Badge>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {MISSIONS.map((m, i) => (
              <Badge key={m.id}
                variant={completedMissions.includes(i) ? 'default' : currentMission === i ? 'secondary' : 'outline'}
                className={`cursor-pointer text-xs ${currentMission === i ? 'ring-2 ring-primary/50' : ''}`}
                onClick={() => setCurrentMission(i)} data-testid={`mission-${m.id}`}
              >
                {completedMissions.includes(i) && <Star className="w-3 h-3 mr-1 fill-yellow-400 text-yellow-400" />}
                {m.name.length > 10 ? `M${m.id}` : m.name}
              </Badge>
            ))}
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-6">
        <motion.div key={currentMission} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
          className="flex items-center gap-3 mb-6 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
          <Target className="w-5 h-5 text-amber-400 shrink-0" />
          <div>
            <span className="font-semibold text-sm">{mission.name}:</span>
            <span className="text-sm text-muted-foreground ml-2">{mission.description}</span>
          </div>
          <div className="ml-auto flex gap-1">
            {[...Array(mission.stars)].map((_, i) => (
              <Star key={i} className={`w-4 h-4 ${completedMissions.includes(currentMission) ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground/30'}`} />
            ))}
          </div>
        </motion.div>

        <p className="text-sm text-muted-foreground mb-4">Drag events to arrange them in chronological order (earliest first):</p>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={userOrder.map(e => e.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2 relative">
              {/* Timeline line */}
              <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-amber-500/30 via-amber-500/20 to-amber-500/10 rounded-full" />

              {userOrder.map((event, i) => {
                const isCorrect = event.order === i + 1;
                return (
                  <div key={event.id} className="relative pl-10">
                    <div className={`absolute left-[18px] top-4 w-4 h-4 rounded-full border-2 z-10 ${
                      isChecked && isCorrect ? 'bg-green-500 border-green-500' :
                      isChecked && !isCorrect ? 'bg-red-500 border-red-500' :
                      'bg-card border-amber-500/50'
                    }`}>
                      <span className="text-[8px] text-white font-bold flex items-center justify-center h-full">{i + 1}</span>
                    </div>
                    <DraggableEvent event={event} isCorrect={isCorrect} isChecked={isChecked} />
                  </div>
                );
              })}
            </div>
          </SortableContext>
        </DndContext>

        <div className="flex items-center gap-3 mt-6">
          <Button onClick={checkOrder} disabled={isChecked && result === 'correct'} data-testid="check-timeline-btn">
            <Check className="w-4 h-4 mr-2" /> Check Order
          </Button>
          <Button variant="outline" onClick={shuffleEvents} data-testid="shuffle-timeline-btn">
            <RotateCcw className="w-4 h-4 mr-2" /> Shuffle
          </Button>
          <AnimatePresence>
            {result === 'correct' && (
              <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-500/10 border border-green-500/30 text-green-400 text-sm font-medium">
                <Check className="w-4 h-4" /> Perfect chronological order!
              </motion.div>
            )}
            {result === 'incorrect' && (
              <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm font-medium">
                <X className="w-4 h-4" /> Some events are out of order. Green = correct position.
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="mt-6 p-4 rounded-xl bg-muted/30 border border-border/30">
          <h4 className="text-xs font-semibold flex items-center gap-1 mb-2 text-muted-foreground">
            <Info className="w-3 h-3" /> Indian History (NCERT)
          </h4>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Indian history spans thousands of years — from the Indus Valley Civilization and the Vedic Age (2500-1500 BCE),
            through the Maurya and Gupta golden ages, the valiant Rajput and regional resistance against foreign invasions
            (Bappa Rawal, Prithviraj Chauhan, Rana Sanga, Hemu, Maharana Pratap, Lachit Borphukan), the rise of the
            Marathas under Shivaji, the British colonial period, and finally independence in 1947. Drag events to learn
            the chronological flow of key historical milestones.
          </p>
        </div>
      </div>
    </div>
  );
}
